// src/hooks/useTranslationStatus.js
import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000/api";

/**
 * Global in-memory caches / inflight maps shared across hook instances
 * Key format: `${slug}::${lang}`
 */
const cache = new Map(); // key -> translation payload
const inflight = new Map(); // key -> promise
const lastRequested = new Map(); // key -> timestamp ms (for simple rate-limiting)

/**
 * useTranslationStatus hook
 *
 * @param {string|null} slug - article slug or id
 * @param {string} lang - target language (e.g. "en", "fr")
 * @param {object} opts
 *   - sourceLang: optional source language to skip fetching when same
 *   - pollOnPending: boolean (default true) -> poll until pending becomes done
 *   - pollInterval: ms (default 3000)
 *   - maxPollAttempts: number (default 20)
 *   - rateLimitMs: ms per key to avoid frequent auto-creation (default 60000)
 *   - authToken: optional JWT token string to send in Authorization header (default: read localStorage.access_token if present)
 *   - withCredentials: boolean to include cookies (default: false)
 *
 * Returns:
 * { translation, loading, error, requesting, requestTranslation, refetch }
 */
export default function useTranslationStatus(slug, lang, opts = {}) {
  const {
    sourceLang = null,
    pollOnPending = true,
    pollInterval = 3000,
    maxPollAttempts = 20,
    rateLimitMs = 60 * 1000,
    authToken = null,
    withCredentials = false,
  } = opts;

  const [translation, setTranslation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState(null);

  // refs to keep state across async boundaries
  const mountedRef = useRef(true);
  const pollCountRef = useRef(0);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      try {
        abortControllerRef.current?.abort();
      } catch {}
    };
  }, []);

  const keyFor = (s, l) => `${s}::${l}`;

  /* ---- helper to build axios config for requests ---- */
  const buildAxiosConfig = (controller) => {
    const headers = { "Content-Type": "application/json" };
    let token = authToken;
    try {
      if (!token && typeof window !== "undefined") {
        token = localStorage.getItem("access_token") || localStorage.getItem("token") || null;
      }
    } catch {}
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return {
      signal: controller?.signal,
      timeout: 10000,
      headers,
      withCredentials: !!withCredentials,
    };
  };

  /**
   * fetchOnce: attempts to GET translations endpoint for slug/lang.
   * Uses cache + inflight dedupe.
   *
   * Note: backend GET /articles/<slug>/translations/?lang=xx will auto-create pending record
   */
  // inside useTranslationStatus.js -> replace the existing fetchOnce function with this:

const fetchOnce = async (s, l, controller) => {
  const key = keyFor(s, l);

  // rate-limit: if we requested recently and no cached payload, avoid hammering backend
  const last = lastRequested.get(key) || 0;
  const now = Date.now();

  // get any previously cached payload (could be 'done', 'pending', or 'missing')
  const lastPayload = cache.get(key);

  // Only throttle when we have no good cached payload (i.e. nothing or 'missing')
  if (now - last < rateLimitMs && (!lastPayload || lastPayload.status === "missing")) {
    // too soon since last request and no cached result: return a 'pending' placeholder
    const pending = { status: "pending", _note: "throttled_client" };
    // do NOT cache this placeholder globally (to allow later real fetch), but return it to UI
    return pending;
  }

  // use cache if present
  if (cache.has(key)) {
    return cache.get(key);
  }

  // reuse inflight promise if exists
  if (inflight.has(key)) {
    return inflight.get(key);
  }

  // mark lastRequested now (we're about to call)
  lastRequested.set(key, now);

  const p = (async () => {
    try {
      // Prefer the read-only status endpoint which does NOT auto-create records.
      // Fall back to the /translations/ endpoint if status endpoint is not available.
      const statusUrl = `${API_BASE}/articles/${encodeURIComponent(s)}/translation_status/?lang=${encodeURIComponent(l)}`;
      let res;
      try {
        // try read-only status first
        res = await axios.get(statusUrl, buildAxiosConfig(controller));
      } catch (errStatus) {
        // fallback to legacy endpoint (may auto-create)
        const url = `${API_BASE}/articles/${encodeURIComponent(s)}/translations/?lang=${encodeURIComponent(l)}`;
        res = await axios.get(url, buildAxiosConfig(controller));
      }

      const data = res?.data;
      let payload = null;
      if (!data) {
        payload = { status: "missing" };
      } else if (Array.isArray(data)) {
        payload = data.length > 0 ? data[0] : { status: "missing" };
      } else {
        payload = data;
      }

      // normalize some server shapes
      if (payload && payload.status && String(payload.status) === "missing") {
        payload = { status: "missing", ...(payload || {}) };
      }

      cache.set(key, payload);
      return payload;
    } catch (err) {
      // capture HTTP errors gracefully
      const status = err?.response?.status;
      const detail = err?.response?.data || err?.message || String(err);
      const payload = status === 404 ? { status: "missing" } : { status: "missing", _error: detail };
      // cache the failure to avoid immediate retries
      cache.set(key, payload);
      return payload;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, p);
  return p;
};

  const doFetch = async (s, l) => {
    if (!s || !l) return null;
    // skip if same language
    if (sourceLang && l && sourceLang.toLowerCase() === l.toLowerCase()) {
      const k = keyFor(s, l);
      cache.delete(k);
      setTranslation(null);
      setError(null);
      setLoading(false);
      return null;
    }

    setLoading(true);
    setError(null);

    // create controller per fetch so we can abort
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const payload = await fetchOnce(s, l, controller);
      if (!mountedRef.current) return null;
      setTranslation(payload);
      return payload;
    } catch (err) {
      if (!mountedRef.current) return null;
      setError(err?.response?.data || err.message || "Failed to fetch");
      setTranslation(null);
      return null;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  // public refetch function: clears cache and fetches anew (ignores rateLimit)
  const refetch = async () => {
    if (!slug || !lang) return;
    const key = keyFor(slug, lang);
    cache.delete(key);
    lastRequested.delete(key);
    try {
      await doFetch(slug, lang);
    } catch {}
  };

  /**
   * requestTranslation: explicit POST to create/requeue translation (requires auth on server)
   * POST /articles/<slug>/translations/  body: { "target_lang": lang } 
   */
  const requestTranslation = async () => {
    if (!slug || !lang) return;
    setRequesting(true);
    setError(null);
    const key = keyFor(slug, lang);

    // immediately show pending in UI (optimistic)
    const pending = { status: "pending" };
    cache.set(key, pending);
    lastRequested.set(key, Date.now());
    setTranslation(pending);

    try {
      const controller = new AbortController();
      const cfg = buildAxiosConfig(controller);
      // increase timeout to 60s for this POST (backend may enqueue / call external API)
      cfg.timeout = 60000;

      // POST to server to request translation (server should respond quickly; if it does long work it should enqueue)
      // endpoint name may vary — adjust if your API expects "/request_translation/" or "/translations/".
      // This code tries /translations/ first then falls back to /request_translation/
      let url = `${API_BASE}/articles/${encodeURIComponent(slug)}/translations/`;
      try {
        await axios.post(url, { target_lang: lang }, cfg);
      } catch (err) {
        // fallback if server expects different path
        if (err?.response?.status === 404) {
          url = `${API_BASE}/articles/${encodeURIComponent(slug)}/request_translation/`;
          await axios.post(url, { lang: lang }, cfg);
        } else {
          throw err;
        }
      }

      // after request, trigger a refetch (non-blocking)
      try {
        await refetch();
      } catch {}
      return pending;
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data || err?.message || String(err);
      setError(detail);
      const payload = { status: "missing", _error: detail };
      cache.set(key, payload);
      setTranslation(payload);
      throw err;
    } finally {
      if (mountedRef.current) setRequesting(false);
    }
  };

  // initial fetch when slug/lang change
  useEffect(() => {
    if (!slug || !lang) return;
    // call doFetch (which uses cache + inflight)
    doFetch(slug, lang).then(() => {
      pollCountRef.current = 0;
    });
    // cleanup: abort pending fetch
    return () => {
      try {
        abortControllerRef.current?.abort();
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, lang, sourceLang]);

  // polling effect: when translation becomes pending, poll until done or max attempts
  useEffect(() => {
    if (!slug || !lang) return;
    if (!pollOnPending) return;
    const key = keyFor(slug, lang);
    let stopped = false;

    const startPolling = async () => {
      // only poll if current translation indicates pending
      const t = cache.get(key) || translation;
      if (!t || t.status !== "pending") return;

      pollCountRef.current = 0;

      while (!stopped && pollCountRef.current < maxPollAttempts) {
        // wait interval
        await new Promise((r) => setTimeout(r, pollInterval));
        pollCountRef.current += 1;
        try {
          // force refetch (clear cache to ensure fresh)
          cache.delete(key);
          const payload = await doFetch(slug, lang);
          if (!mountedRef.current) return;
          if (payload && payload.status !== "pending") {
            // done or missing => stop polling
            return;
          }
        } catch {
          // ignore transient errors, continue polling until max attempts
        }
      }
    };

    // start only if cached translation is pending OR current in-state is pending
    const current = cache.get(key);
    if (current && current.status === "pending") {
      startPolling();
    } else if (translation && translation.status === "pending") {
      startPolling();
    }

    return () => {
      stopped = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translation && translation.status, slug, lang, pollOnPending]);

  return {
    translation,
    loading,
    error,
    requesting,
    requestTranslation,
    refetch,
  };
}
